import { useState, useCallback, useEffect, useMemo } from 'react';
export const useSearchablePagination = (
  queryHook,
  queryParams,
  options = {}
) => {
  const {
    itemsPerPage = 5,
    enableSearch = true,
    enablePagination = true,
    debounceTime = 500,
    onError = null,
    initialSearchTerm = ''
  } = options;

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialSearchTerm);
  const [isSearching, setIsSearching] = useState(false);

  // Memoizar los argumentos de la query
  const queryArgs = useMemo(() => {
    // Si es el hook de marcas, verificar si necesita paginación
    const isVehicleBrandsHook = queryHook.name === 'useVehicleBrands' || 
                               queryParams.length === 0;
    
    if (isVehicleBrandsHook) {
      return [
        currentPage,
        itemsPerPage,
        enableSearch ? debouncedSearchTerm : '',
        enablePagination
      ];
    }
    
    // Para otros hooks (como useUsersByRole)
    return [
      ...queryParams,
      currentPage,
      itemsPerPage,
      enableSearch ? debouncedSearchTerm : ''
    ];
  }, [queryParams, currentPage, itemsPerPage, enableSearch, enablePagination, debouncedSearchTerm, queryHook]);

  // Resto del código permanece igual...
  useEffect(() => {
    if (searchTerm !== debouncedSearchTerm) {
      setIsSearching(true);
    }

    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setIsSearching(false);
      
      if (searchTerm !== debouncedSearchTerm && currentPage !== 1) {
        setCurrentPage(1);
      }
    }, debounceTime);

    return () => {
      clearTimeout(timer);
      setIsSearching(false);
    };
  }, [searchTerm, debounceTime, debouncedSearchTerm, currentPage]);

  const queryResult = queryHook(...queryArgs);

  useEffect(() => {
    if (queryResult.isError && onError) {
      onError(queryResult.error);
    }
  }, [queryResult.isError, queryResult.error, onError]);

  const handleSearch = useCallback((term) => {
    setSearchTerm(term);
  }, []);

  const handlePageChange = useCallback((page) => {
    if (page >= 1 && page <= Math.ceil((queryResult.data?.totalItems || queryResult.data?.total || 0) / itemsPerPage)) {
      setCurrentPage(page);
    }
  }, [itemsPerPage, queryResult.data]);

  const resetPagination = useCallback(() => {
    setCurrentPage(1);
    setSearchTerm(initialSearchTerm);
    setDebouncedSearchTerm(initialSearchTerm);
    setIsSearching(false);
  }, [initialSearchTerm]);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setCurrentPage(1);
    setIsSearching(false);
  }, []);

  return {
    // Compatibilidad con diferentes formatos de respuesta
    data: queryResult.data?.data || [],
    totalItems: queryResult.data?.totalItems || queryResult.data?.total || 0,
    totalPages: queryResult.data?.totalPages || Math.ceil((queryResult.data?.totalItems || queryResult.data?.total || 0) / itemsPerPage),
    
    currentPage,
    searchTerm,
    debouncedSearchTerm,
    isLoading: queryResult.isLoading || isSearching,
    isSearching,
    isError: queryResult.isError,
    error: queryResult.error,
    
    itemsPerPage,
    enableSearch,
    enablePagination,
    
    handleSearch,
    handlePageChange,
    resetPagination,
    clearSearch,
    
    queryResult
  };
};